import { v4 as uuid } from "uuid";

/**
 * Writes the user-approved quotes and anecdotes from an interview to the
 * Quotes / Anecdotes collections. Only what the user accepted in the review
 * step reaches Firestore — nothing is written automatically. Empty arrays write
 * nothing (a fully-rejected review is a no-op, not an error).
 *
 * Position continues the tenant's existing numbering (count + index + 1), the
 * same shape the storytelling tabs used to write inline before the review step.
 *
 * @param {object} args
 * @param {import('firebase/compat/app').default.firestore.Firestore} args.db - Firestore instance
 * @param {string} args.client - tenant CompagnyID
 * @param {*} args.timestamp - Firestore server timestamp sentinel
 * @param {string} [args.interviewName] - stored as `Name` on each doc
 * @param {string[]} [args.quotes] - approved quote texts
 * @param {string[]} [args.anekdotes] - approved anecdote texts
 * @returns {Promise<{savedQuotes: number, savedAnecdotes: number}>}
 */
const saveQuoteAnecdoteProposals = async ({
    db,
    client,
    timestamp,
    interviewName = "",
    quotes = [],
    anekdotes = [],
}) => {
    const [existingQuotes, existingAnecdotes] = await Promise.all([
        db.collection("Quotes").where("CompagnyID", "==", client).get(),
        db.collection("Anecdotes").where("CompagnyID", "==", client).get(),
    ]);

    const quoteSaves = quotes.map((quote, index) =>
        db.collection("Quotes").doc().set({
            Quote: quote,
            Name: interviewName,
            ID: uuid(),
            Timestamp: timestamp,
            CompagnyID: client,
            Position: existingQuotes.size + index + 1,
            Type: "Quote",
        })
    );

    const anecdoteSaves = anekdotes.map((anekdote, index) =>
        db.collection("Anecdotes").doc().set({
            Anecdote: anekdote,
            Name: interviewName,
            ID: uuid(),
            Timestamp: timestamp,
            CompagnyID: client,
            Position: existingAnecdotes.size + index + 1,
            Type: "Anecdote",
        })
    );

    await Promise.all([...quoteSaves, ...anecdoteSaves]);

    return { savedQuotes: quotes.length, savedAnecdotes: anekdotes.length };
};

export default saveQuoteAnecdoteProposals;
